// Objektive Mix-Messung, rein rechnerisch und lokal (keine externen Dienste):
//  - Lautheit nach ITU-R BS.1770-4 / EBU R128: Integrated (Gating −70 LUFS / −10 LU), Short-Term (3 s), Loudness Range (EBU Tech 3342)
//  - True Peak (4-fach überabgetastet), Sample Peak, PLR (Abstand Spitze zu Lautheit)
//  - Klangbalance in sieben Bändern, Stereo-Korrelation, Mono-Verlust
//  - Merkmale einzelner Spuren (Bassanteil, Schwerpunkt, Crest, Anschläge, Tonhaftigkeit) für die Rollenschätzung
// Die Auswahl dieser Größen folgt der objektiven Bewertung in der Literatur zum automatischen Mischen
// (RMS, Integrated Loudness, Loudness Range, True Peak, spektrale Deskriptoren). Kein Messwert ersetzt das Hören:
// sie dienen dem Vergleich zwischen Fassungen desselben Songs, nicht als Qualitätsnote.
import { fft } from '../audio/stretch.ts';
import { yin } from '../edit/pitch.ts';

export const BANDS = [
  { id: 'sub', label: 'Sub', lo: 20, hi: 60 },
  { id: 'bass', label: 'Bass', lo: 60, hi: 250 },
  { id: 'lowmid', label: 'Tiefe Mitten', lo: 250, hi: 500 },
  { id: 'mid', label: 'Mitten', lo: 500, hi: 2000 },
  { id: 'himid', label: 'Obere Mitten', lo: 2000, hi: 4000 },
  { id: 'pres', label: 'Präsenz', lo: 4000, hi: 8000 },
  { id: 'air', label: 'Luft', lo: 8000, hi: 20000 },
] as const;
export type BandId = (typeof BANDS)[number]['id'];

export interface MixReport {
  /** Integrated Loudness, LUFS */
  lufs: number;
  /** Loudness Range, LU */
  lra: number;
  /** größte Short-Term-Lautheit, LUFS */
  stMax: number;
  truePeak: number;
  samplePeak: number;
  /** Peak to Loudness Ratio: True Peak − Integrated */
  plr: number;
  /** Stereo-Korrelation −1 … +1 */
  corr: number;
  /** Pegelverlust bei Mono-Summe, dB (0 = monotauglich, stark negativ = Auslöschung) */
  monoLoss: number;
  /** Anteil je Band am Gesamtspektrum, dB */
  bands: Record<BandId, number>;
  seconds: number;
}

const LOG = (x: number) => (x > 0 ? 10 * Math.log10(x) : -Infinity);
const lufsOf = (ms: number) => (ms > 0 ? -0.691 + 10 * Math.log10(ms) : -Infinity);

/** Biquad (Direktform I) über ein ganzes Signal */
function biquad(x: Float32Array, b0: number, b1: number, b2: number, a1: number, a2: number): Float32Array {
  const y = new Float32Array(x.length);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const v = x[i], o = b0 * v + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = v; y2 = y1; y1 = o; y[i] = o;
  }
  return y;
}

/** K-Filter nach BS.1770 (Koeffizienten wie im Lautheitsmesser der Wiedergabe) */
export function kWeight(x: Float32Array, sr: number): Float32Array {
  let K = Math.tan((Math.PI * 1681.974450955533) / sr);
  const Q1 = 0.7071752369554196, Vh = Math.pow(10, 3.999843853973347 / 20), Vb = Math.pow(Vh, 0.4996667741545416);
  let a0 = 1 + K / Q1 + K * K;
  const s1 = biquad(x, (Vh + (Vb * K) / Q1 + K * K) / a0, (2 * (K * K - Vh)) / a0, (Vh - (Vb * K) / Q1 + K * K) / a0, (2 * (K * K - 1)) / a0, (1 - K / Q1 + K * K) / a0);
  K = Math.tan((Math.PI * 38.13547087602444) / sr);
  const Q2 = 0.5003270373238773;
  a0 = 1 + K / Q2 + K * K;
  return biquad(s1, 1, -2, 1, (2 * (K * K - 1)) / a0, (1 - K / Q2 + K * K) / a0);
}

/** Laufende Summe der K-gewichteten Energie aller Kanäle: Energie eines beliebigen Fensters in O(1) */
function energyPrefix(ch: Float32Array[], sr: number): Float64Array {
  const n = ch[0]?.length ?? 0, pre = new Float64Array(n + 1);
  const k = ch.map((c) => kWeight(c, sr));
  for (let i = 0; i < n; i++) { let e = 0; for (const c of k) e += c[i] * c[i]; pre[i + 1] = pre[i] + e; }
  return pre;
}
const meanSq = (pre: Float64Array, a: number, b: number) => (b > a ? (pre[b] - pre[a]) / (b - a) : 0);

/** Fensterwerte (Energie) mit Länge win und Abstand hop, in Sekunden */
function windows(pre: Float64Array, sr: number, win: number, hop: number): number[] {
  const n = pre.length - 1, w = Math.round(win * sr), h = Math.round(hop * sr), out: number[] = [];
  if (n < w) { if (n > 0) out.push(meanSq(pre, 0, n)); return out; }
  for (let a = 0; a + w <= n; a += h) out.push(meanSq(pre, a, a + w));
  return out;
}

/** Integrated Loudness mit absolutem (−70 LUFS) und relativem (−10 LU) Gate */
function integratedOf(blocks: number[]): number {
  const abs = blocks.filter((z) => lufsOf(z) > -70);
  if (!abs.length) return -Infinity;
  const rel = lufsOf(abs.reduce((a, b) => a + b, 0) / abs.length) - 10;
  const g = abs.filter((z) => lufsOf(z) > rel);
  return g.length ? lufsOf(g.reduce((a, b) => a + b, 0) / g.length) : -Infinity;
}

/** Integrated Loudness (LUFS) eines Signals */
export function integratedLoudness(ch: Float32Array[], sr: number): number {
  return integratedOf(windows(energyPrefix(ch, sr), sr, 0.4, 0.1));
}

/** Loudness Range nach EBU Tech 3342: Short-Term-Werte, Gate −70 LUFS und −20 LU, Spanne P10 … P95 */
function lraOf(st: number[]): number {
  const abs = st.filter((z) => lufsOf(z) > -70);
  if (abs.length < 2) return 0;
  const rel = lufsOf(abs.reduce((a, b) => a + b, 0) / abs.length) - 20;
  const v = abs.map(lufsOf).filter((l) => l > rel).sort((a, b) => a - b);
  if (v.length < 2) return 0;
  const q = (p: number) => v[Math.min(v.length - 1, Math.max(0, Math.round(p * (v.length - 1))))];
  return q(0.95) - q(0.1);
}

/** Interpolationsfilter für 4-fache Überabtastung (gefensterter Sinc, 32 Stützstellen je Phase) */
const TP_TAPS = 16;
const TP_KERNEL = [1, 2, 3].map((ph) => {
  const f = ph / 4, k = new Float64Array(2 * TP_TAPS);
  for (let j = 0; j < 2 * TP_TAPS; j++) {
    const t = j - TP_TAPS + 1 - f, w = 0.5 + 0.5 * Math.cos((Math.PI * t) / (TP_TAPS + 0.5));
    k[j] = (t === 0 ? 1 : Math.sin(Math.PI * t) / (Math.PI * t)) * w;
  }
  return k;
});

/** True Peak (dBTP): Zwischenwerte nur dort berechnen, wo das Signal in die Nähe der Spitze kommt */
export function truePeak(ch: Float32Array[]): { tp: number; sp: number } {
  let sp = 0;
  for (const c of ch) for (let i = 0; i < c.length; i++) { const a = c[i] < 0 ? -c[i] : c[i]; if (a > sp) sp = a; }
  let tp = sp;
  const near = sp * 0.5;
  for (const c of ch) {
    for (let i = TP_TAPS; i < c.length - TP_TAPS; i++) {
      const a = Math.abs(c[i]), b = Math.abs(c[i + 1]);
      if (a < near && b < near) continue;
      for (const k of TP_KERNEL) {
        let s = 0;
        for (let j = 0; j < 2 * TP_TAPS; j++) s += c[i - TP_TAPS + 1 + j] * k[j];
        if (Math.abs(s) > tp) tp = Math.abs(s);
      }
    }
  }
  return { tp: 20 * Math.log10(tp || 1e-12), sp: 20 * Math.log10(sp || 1e-12) };
}

/** Mittleres Leistungsspektrum (Hann, 4096 Punkte), höchstens maxFrames gleichmäßig verteilte Blöcke */
export function powerSpectrum(ch: Float32Array[], sr: number, maxFrames = 300): { p: Float64Array; df: number } {
  const N = 4096, n = ch[0]?.length ?? 0, p = new Float64Array(N / 2 + 1);
  const frames = Math.max(1, Math.floor(n / N)), step = Math.max(1, Math.floor(frames / maxFrames));
  const re = new Float64Array(N), im = new Float64Array(N), win = new Float64Array(N);
  for (let i = 0; i < N; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / N);
  for (let f = 0; f < frames; f += step) {
    for (const c of ch) {
      const a = f * N;
      for (let i = 0; i < N; i++) { re[i] = (a + i < n ? c[a + i] : 0) * win[i]; im[i] = 0; }
      fft(re, im);
      for (let k = 0; k <= N / 2; k++) p[k] += re[k] * re[k] + im[k] * im[k];
    }
  }
  return { p, df: sr / N };
}

/** Energieanteil je Band in dB (Summe aller Bänder = 0 dB) */
export function bandBalance(ch: Float32Array[], sr: number): Record<BandId, number> {
  const { p, df } = powerSpectrum(ch, sr);
  const e = BANDS.map((b) => { let s = 0; for (let k = Math.ceil(b.lo / df); k <= Math.min(p.length - 1, Math.floor(b.hi / df)); k++) s += p[k]; return s; });
  const tot = e.reduce((a, b) => a + b, 0) || 1e-30;
  return Object.fromEntries(BANDS.map((b, i) => [b.id, Math.max(-60, LOG(e[i] / tot))])) as Record<BandId, number>;
}

/** Stereo-Korrelation und Pegelverlust der Mono-Summe */
export function stereoInfo(ch: Float32Array[]): { corr: number; monoLoss: number } {
  if (ch.length < 2) return { corr: 1, monoLoss: 0 };
  const [l, r] = ch;
  let lr = 0, ll = 0, rr = 0, mm = 0;
  for (let i = 0; i < l.length; i++) { lr += l[i] * r[i]; ll += l[i] * l[i]; rr += r[i] * r[i]; const m = (l[i] + r[i]) / 2; mm += m * m; }
  const corr = ll > 0 && rr > 0 ? lr / Math.sqrt(ll * rr) : 1;
  const monoLoss = ll + rr > 0 ? Math.max(-40, LOG((2 * mm) / (ll + rr))) : 0;
  return { corr, monoLoss };
}

/** Vollständige Messung eines Mixes */
export function measureMix(ch: Float32Array[], sr: number): MixReport {
  const pre = energyPrefix(ch, sr);
  const lufs = integratedOf(windows(pre, sr, 0.4, 0.1));
  const st = windows(pre, sr, 3, 0.1);
  const { tp, sp } = truePeak(ch);
  const { corr, monoLoss } = stereoInfo(ch);
  return { lufs, lra: lraOf(st), stMax: Math.max(-Infinity, ...st.map(lufsOf)), truePeak: tp, samplePeak: sp, plr: tp - lufs, corr, monoLoss, bands: bandBalance(ch, sr), seconds: (ch[0]?.length ?? 0) / sr };
}

/**
 * Repräsentative Stelle des Songs: das Fenster mit der größten Lautheit (meist Refrain, dort spielen die meisten Instrumente).
 * In der Literatur werden Mixe genau an so einem Ausschnitt von etwa 15 s verglichen.
 */
export function loudestWindow(ch: Float32Array[], sr: number, len = 15, hop = 0.5): number {
  const pre = energyPrefix(ch, sr), n = pre.length - 1, w = Math.round(len * sr), h = Math.round(hop * sr);
  if (n <= w) return 0;
  let best = 0, bestE = -1;
  for (let a = 0; a + w <= n; a += h) { const e = pre[a + w] - pre[a]; if (e > bestE) { bestE = e; best = a; } }
  return best / sr;
}

/** Merkmale einer einzelnen Spur im Analysefenster */
export interface TrackFeatures {
  /** Lautheit, solange die Spur spielt (gegatet), LUFS */
  lufs: number;
  rmsDb: number;
  peakDb: number;
  /** Energieanteil unter 120 Hz */
  lowRatio: number;
  /** spektraler Schwerpunkt, Hz */
  centroid: number;
  /** Spitze zu RMS der aktiven Teile, dB */
  crest: number;
  /** Anteil der Zeit, in der die Spur spielt */
  activity: number;
  /** Anschläge pro Sekunde */
  onsetRate: number;
  /** Anteil tonaler (gepitchter) Abschnitte an den aktiven */
  voiced: number;
  /** mittlere Grundfrequenz der tonalen Abschnitte, Hz (0 = keine) */
  f0: number;
  /** Energieanteil über 6 kHz, dB */
  hf: number;
  /** Abstand Median zu leisesten 10 % der 100-ms-Blöcke, dB (klein = gleichbleibender Pegel) */
  spread: number;
  /** Pegel der leisesten 10 % der 100-ms-Blöcke, dBFS */
  floor: number;
}

/**
 * Durchgehendes Rauschen (z. B. offener DI-Eingang, Masseproblem): viel Energie in den Höhen, gleichbleibender Pegel,
 * keine Anschläge, Grundpegel weit über dem üblichen Rauschteppich.
 */
export const noisy = (f: TrackFeatures) => f.hf > -8 && f.spread < 4 && f.onsetRate < 0.5 && f.floor > -45;

export function trackFeatures(ch: Float32Array[], sr: number): TrackFeatures {
  const n = ch[0]?.length ?? 0;
  const mono = new Float32Array(n);
  for (const c of ch) for (let i = 0; i < n; i++) mono[i] += c[i] / ch.length;
  let sum = 0, pk = 0;
  for (const c of ch) for (let i = 0; i < n; i++) { sum += c[i] * c[i]; const a = Math.abs(c[i]); if (a > pk) pk = a; }
  const rms = Math.sqrt(sum / Math.max(1, n * ch.length));
  // Hüllkurve in 10-ms-Blöcken: Aktivität, Crest der aktiven Teile, Anschläge
  const B = Math.round(sr * 0.01), nb = Math.floor(n / B), env = new Float64Array(nb);
  for (let b = 0; b < nb; b++) { let e = 0; for (let i = b * B; i < (b + 1) * B; i++) e += mono[i] * mono[i]; env[b] = e / B; }
  const envMax = Math.max(1e-20, ...env);
  const gate = envMax * Math.pow(10, -45 / 10);
  let act = 0, actE = 0;
  for (const e of env) if (e > gate && e > 1e-10) { act++; actE += e; }
  const actRms = act ? Math.sqrt(actE / act) : 0;
  let onsets = 0, last = -100;
  const db = Array.from(env, (e) => LOG(e + 1e-20));
  for (let b = 3; b < nb; b++) {
    const rise = db[b] - Math.min(db[b - 1], db[b - 2], db[b - 3]);
    if (rise > 9 && env[b] > gate && b - last > 5) { onsets++; last = b; }
  }
  const { p, df } = powerSpectrum([mono], sr, 120);
  let tot = 0, low = 0, cen = 0, hi = 0;
  for (let k = 1; k < p.length; k++) { tot += p[k]; cen += p[k] * k * df; if (k * df < 120) low += p[k]; if (k * df >= 6000) hi += p[k]; }
  // Pegel je 100 ms: Grundpegel und Gleichmäßigkeit
  const blk: number[] = [];
  for (let b = 0; b + 10 <= nb; b += 10) { let e = 0; for (let j = b; j < b + 10; j++) e += env[j]; blk.push(LOG(e / 10 + 1e-20)); }
  blk.sort((a, b) => a - b);
  const p10 = blk.length ? blk[Math.floor(blk.length * 0.1)] : -200, med = blk.length ? blk[Math.floor(blk.length / 2)] : -200;
  // Tonhaftigkeit über YIN (höchstens 20 s)
  const pt = yin(n > sr * 20 ? mono.subarray(0, sr * 20) : mono, sr);
  let vAct = 0, vOn = 0; const fs: number[] = [];
  for (let k = 0; k < pt.f0.length; k++) {
    const b = Math.floor((pt.off + k * pt.hop) / 0.01);
    if (b >= nb || env[b] <= gate * 10) continue; // nur deutlich aktive Abschnitte zählen
    vAct++;
    if (pt.f0[k] > 0 && pt.conf[k] > 0.5) { vOn++; fs.push(pt.f0[k]); }
  }
  fs.sort((a, b) => a - b);
  const sec = n / sr;
  return {
    lufs: integratedLoudness(ch, sr),
    rmsDb: 20 * Math.log10(rms || 1e-12),
    peakDb: 20 * Math.log10(pk || 1e-12),
    lowRatio: tot ? low / tot : 0,
    centroid: tot ? cen / tot : 0,
    crest: actRms > 0 ? 20 * Math.log10(pk / actRms) : 0,
    activity: nb ? act / nb : 0,
    onsetRate: sec > 0 ? onsets / sec : 0,
    voiced: vAct ? vOn / vAct : 0,
    f0: fs.length ? fs[Math.floor(fs.length / 2)] : 0,
    hf: tot ? Math.max(-60, LOG(hi / tot)) : -60,
    spread: med - p10,
    floor: p10,
  };
}

export type Role = 'kick' | 'snare' | 'drums' | 'bass' | 'vox' | 'bgv' | 'keys' | 'guitar' | 'strings' | 'lead' | 'other';

/**
 * Rolle aus dem Klang schätzen, wenn der Spurname nichts verrät (z. B. „Audio 3“).
 * Bewusst einfache, nachvollziehbare Regeln; die Sicherheit ist niedrig und der Mensch korrigiert im Auto-Mix.
 */
export function guessRole(f: TrackFeatures): { role: Role; conf: number } {
  if (f.activity < 0.02 || !Number.isFinite(f.lufs) || noisy(f)) return { role: 'other', conf: 0.3 };
  const percussive = f.crest > 10 && f.onsetRate > 1 && f.voiced < 0.25;
  if (percussive && f.lowRatio > 0.35) return { role: 'kick', conf: 0.55 };
  if (percussive && f.centroid > 2500) return { role: 'drums', conf: 0.5 };
  if (percussive) return { role: 'snare', conf: 0.4 };
  if (f.lowRatio > 0.35 && f.f0 > 0 && f.f0 < 180) return { role: 'bass', conf: 0.6 };
  if (f.voiced > 0.55 && f.f0 >= 100 && f.f0 <= 900 && f.lowRatio < 0.15 && f.centroid < 3000) return { role: 'vox', conf: 0.45 };
  return { role: 'other', conf: 0.3 };
}
